import React, { useEffect } from 'react';
import { Button, Col, Row } from 'antd';
import { ArrowRightOutlined } from '@ant-design/icons';
import { Link, useLocation } from 'react-router-dom';
import {
  COMMITMENTS,
  COMMITMENTS_SECTION,
  FEATURES_SECTION,
  LANDING_CTA_BAND,
  LANDING_HERO,
} from '../content/landingContent';
import ReadinessRail from './ReadinessRail';
import FeatureGrid from './FeatureGrid';
import FeatureIcon from './FeatureIcon';
import HeroProductPreview from './HeroProductPreview';
import HeroDifferentiators from './HeroDifferentiators';
import LandingSectionHead from './LandingSectionHead';
import LandingAccessCta from './LandingAccessCta';
import { scrollToSection } from '../utils/scrollToSection';

const MarketingLandingPage: React.FC = () => {
  const location = useLocation();

  useEffect(() => {
    const state = location.state as { scrollTo?: string } | null;
    const target = state?.scrollTo;
    if (target) {
      window.setTimeout(() => scrollToSection(target), 120);
    }
  }, [location.state]);

  return (
    <div className="cp-landing-page">
      <section className="cp-landing-hero">
        <div className="cp-landing-hero__glow" aria-hidden />
        <div className="cp-landing-container">
          <div className="cp-landing-hero__grid">
          <div className="cp-landing-hero__copy">
            <span className="cp-landing-eyebrow">{LANDING_HERO.eyebrow}</span>
            <h1 className="cp-landing-hero__title">
              {LANDING_HERO.title}
              <span className="cp-landing-hero__title-accent">{LANDING_HERO.titleAccent}</span>
            </h1>
            <p className="cp-landing-hero__subtitle">{LANDING_HERO.subtitle}</p>
            <p className="cp-landing-hero__promise">{LANDING_HERO.customization}</p>
            <div className="cp-landing-hero__actions">
              <Link to="/signup">
                <Button
                  type="primary"
                  size="large"
                  className="cp-landing-btn-primary"
                  icon={<ArrowRightOutlined />}
                >
                  Request access
                </Button>
              </Link>
              <Button
                size="large"
                className="cp-landing-btn-secondary"
                onClick={() => scrollToSection('features')}
              >
                Explore features
              </Button>
            </div>
            <button
              type="button"
              className="cp-landing-hero__readiness-link"
              onClick={() => scrollToSection('when-crm')}
            >
              Sound familiar? Take the 30-second check ↓
            </button>
          </div>
          <div className="cp-landing-hero__visual">
            <HeroProductPreview />
          </div>
          </div>
          <HeroDifferentiators />
        </div>
      </section>

      <section id="features" className="cp-landing-section cp-landing-section--after-hero">
        <div className="cp-landing-container">
          <LandingSectionHead
            eyebrow={FEATURES_SECTION.eyebrow}
            title={FEATURES_SECTION.title}
            lead={FEATURES_SECTION.lead}
            centered
          />
          <FeatureGrid />
        </div>
      </section>

      <ReadinessRail />

      <section className="cp-landing-cta-band" aria-labelledby="landing-cta-title">
        <div className="cp-landing-container cp-landing-cta-band__inner">
          <div>
            <h2 id="landing-cta-title" className="cp-landing-cta-band__title">
              {LANDING_CTA_BAND.title}
            </h2>
            <p className="cp-landing-cta-band__lead">{LANDING_CTA_BAND.lead}</p>
          </div>
          <div className="cp-landing-cta-band__actions">
            <Link to="/signup">
              <Button
                type="primary"
                size="large"
                className="cp-landing-btn-primary cp-landing-btn-primary--inverse"
              >
                {LANDING_CTA_BAND.primaryLabel}
              </Button>
            </Link>
            <Button
              size="large"
              className="cp-landing-btn-secondary cp-landing-btn-secondary--inverse"
              onClick={() => scrollToSection('partnership')}
            >
              {LANDING_CTA_BAND.secondaryLabel}
            </Button>
          </div>
        </div>
      </section>

      <section id="commitments" className="cp-landing-section cp-landing-section--muted">
        <div className="cp-landing-container">
          <LandingSectionHead
            eyebrow={COMMITMENTS_SECTION.eyebrow}
            title={COMMITMENTS_SECTION.title}
            lead={COMMITMENTS_SECTION.lead}
            centered
          />
          <Row gutter={[24, 24]}>
            {COMMITMENTS.map((item) => (
              <Col key={item.title} xs={24} md={8}>
                <article className="cp-landing-trust-card">
                  <span className="cp-landing-trust-card__icon-wrap">
                    <FeatureIcon iconKey={item.iconKey} className="cp-landing-trust-card__icon" />
                  </span>
                  <div>
                    <h3 className="cp-landing-trust-card__title">{item.title}</h3>
                    <p className="cp-landing-trust-card__text">{item.text}</p>
                  </div>
                </article>
              </Col>
            ))}
          </Row>
        </div>
      </section>

      <LandingAccessCta />
    </div>
  );
};

export default MarketingLandingPage;
